import React, { useEffect, useMemo, useState } from "react";
import { Box, Image } from "@chakra-ui/react";
import { ExtractionDataItem } from "../../../api/types";
import { typedSxMap } from "../../../utils/typedSxMap";

const BOX_COLOR = "var(--blue-light-600)";
const BOX_COLOR_EMPTY = "var(--blue-light-600-opacity)";
const BOX_COLOR_VALID = "var(--color-green)";
const BOX_COLOR_ERROR = "var(--color-cell-error)";

const styles = typedSxMap({
  container: {
    position: "relative",
    flex: 2,
    height: "calc(100vh - 94px)",
    display: "flex",
    flexDirection: "column",
  },
  scrollContainer: {
    flex: 1,
    minHeight: 0,
    position: "relative",
  },
  contentWrapper: {
    height: "100%",
    position: "relative",
  },
  contentWrapperScroll: {
    height: "100%",
    position: "relative",
    overflowY: "scroll",
  },
  pdfImage: {
    width: "100%",
  },
  dataItem: {
    transition: "all 0.05s ease-in-out",
  },
  dataItemEmpty: {
    borderColor: BOX_COLOR_EMPTY,
  },
  dataItemValid: {
    borderColor: BOX_COLOR_VALID,
  },
  dataItemError: {
    borderColor: BOX_COLOR_ERROR,
  },
});

type PDFViewerProps = {
  pdfImageUrl?: string;
  data: { [key: string]: ExtractionDataItem };
  selectedDataItem?: ExtractionDataItem | null;
  setSelectedDataItem: (item: ExtractionDataItem | null, key: string) => void;
  onScroll: () => void;
  independentScroll?: boolean;
};

export default function PDFViewer({ data, selectedDataItem, pdfImageUrl, setSelectedDataItem, onScroll, independentScroll = false }: PDFViewerProps) {
  const [zoom, setZoom] = useState(100);
  const [boxColors, setBoxColors] = useState<{ [key: string]: string }>({});

  const boxBorderSizePx = 2;
  const boxBorderSizeAdjustment = 0.008;
  const hoverExpansionPx = 1;

  const adjustBoundingBox = (box: { width: number; height: number; left: number; top: number }) => {
    return {
      width: box.width + boxBorderSizeAdjustment,
      height: box.height + boxBorderSizeAdjustment,
      left: box.left - boxBorderSizeAdjustment / 2,
      top: box.top - boxBorderSizeAdjustment / 2,
    };
  };

  const getBoxColor = (item: ExtractionDataItem) => {
    if (item.table_data) {
      const hasErrors = item.table_data.some((row) => Object.values(row).some((cell) => cell.errors && cell.errors.length > 0));
      return hasErrors ? BOX_COLOR_ERROR : BOX_COLOR_VALID;
    }
    if (selectedDataItem?.page_element?.id === item.page_element?.id) {
      if (item.errors?.length) {
        return BOX_COLOR_ERROR;
      } else if (item.errors === null && item.value) {
        return BOX_COLOR_VALID;
      } else if (!item.value) {
        return BOX_COLOR_EMPTY;
      }
    }
    return BOX_COLOR;
  };

  useEffect(() => {
    const newBoxColors: { [key: string]: string } = {};
    Object.entries(data).forEach(([key, item]) => {
      newBoxColors[key] = selectedDataItem?.page_element?.id === item?.page_element?.id ? getBoxColor(item) : BOX_COLOR;
    });
    setBoxColors(newBoxColors);
  }, [data, selectedDataItem]);

  const dataEntries = useMemo(() => Object.entries(data), [data]);

  return (
    <Box sx={styles.container} className="pdfViewer">
      <Box sx={styles.scrollContainer}>
        <Box sx={independentScroll ? styles.contentWrapperScroll : styles.contentWrapper} onScroll={onScroll}>
          <Box position="relative" width={`${zoom}%`}>
            <Image src={pdfImageUrl} alt={"pdfImage"} transition="opacity 0.3s ease 0s" sx={styles.pdfImage} />
            <Box>
              {dataEntries.map(([key, item], index) => {
                if (!item?.page_element?.bounding_box) return null;
                const { width, height, left, top } = adjustBoundingBox(item.page_element.bounding_box);
                const id = `item-${item?.page_element?.id}`;
                const boxColor = boxColors[key] || BOX_COLOR;
                const isSelected = selectedDataItem?.page_element?.id === item?.page_element?.id;

                return (
                  <Box
                    id={id}
                    key={index}
                    position="absolute"
                    left={`calc(${left * 100}% - ${hoverExpansionPx}px)`}
                    top={`calc(${top * 100}% - ${hoverExpansionPx}px)`}
                    width={`calc(${width * 100}% + ${hoverExpansionPx * 2}px)`}
                    height={`calc(${height * 100}% + ${hoverExpansionPx * 2}px)`}
                    sx={{
                      ...styles.dataItem,
                      border: `${boxBorderSizePx}px solid ${boxColor}`,
                      ...(isSelected && {
                        boxShadow: `0 0 0 ${hoverExpansionPx}px ${boxColor}`,
                      }),
                      "&:hover": {
                        boxShadow: `0 0 0 ${hoverExpansionPx}px ${boxColor}`,
                      },
                    }}
                    cursor="pointer"
                    onClick={() => setSelectedDataItem(item, key)}
                  />
                );
              })}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
